// src/components/Services.tsx
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { services } from '@/data/services';
import { ChevronDown, MessageCircle } from 'lucide-react';

const Services = () => {
  // On mobile, we'll start with no active card. On desktop, we'll default to 'web'
  const [activeCard, setActiveCard] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const ref = useRef(null);
  const ctaRef = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const isCTAInView = useInView(ctaRef, { once: false, amount: 0.5 });
  const navigate = useNavigate();

  // Reference to track if component is mounted
  const isMounted = useRef(false);

  // Check if we're on mobile on component mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      // Only set default on initial mount for desktop
      if (!mobile && !activeCard && !isMounted.current) {
        setActiveCard('web');
      }

      // Mark as mounted after first run
      isMounted.current = true;
    };

    // Initial check
    checkMobile();

    // Listen for window resize events
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, [activeCard]);

  const handleCardClick = (id: string) => {
    // This is now primarily used for mobile
    setActiveCard(id);
  };

  // Navigate to the specific service tab on the services page
  const navigateToServiceTab = (serviceId: string) => {
    navigate(`/services?tab=${serviceId}`);
  };

  // Scroll to contact section
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // CTA button animation variants
  const pulseVariants = {
    initial: { scale: 1 },
    pulse: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        repeatType: 'reverse' as const,
      },
    },
  };

  const iconVariants = {
    initial: { y: 0 },
    bounce: {
      y: [0, -5, 0],
      transition: {
        duration: 1,
        repeat: Infinity,
        repeatType: 'reverse' as const,
        ease: 'easeInOut',
      },
    },
  };

  const arrowVariants = {
    initial: { y: 0, opacity: 0.6 },
    animate: {
      y: [0, 5, 0],
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.2,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  // Calculate width for expanded and collapsed cards
  // Expanded card takes 70% of the width, remaining cards share 30%
  const getCardWidth = (id: string) => {
    if (!activeCard) return '20%'; // Equal width when none is active
    if (id === activeCard) return '70%'; // Expanded card
    return '7.5%'; // Collapsed card (4 cards * 7.5% = 30%)
  };

  return (
    <section
      id="services"
      className="min-h-screen flex flex-col bg-primary text-white relative">
      <div className="w-full py-28 flex-grow flex flex-col" ref={ref}>
        <div className="text-center mb-16 px-4">
          <motion.h2
            className="text-4xl md:text-6xl font-bold mb-4 uppercase"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}>
            Services
          </motion.h2>
          <motion.div
            className="h-1 bg-secondary mx-auto"
            initial={{ width: 0 }}
            animate={isInView ? { width: 96 } : { width: 0 }}
            transition={{ duration: 0.7 }}
          />
        </div>

        {/* Mobile View (List with Fullscreen Expanded Cards) */}
        <div className="md:hidden flex flex-col space-y-4 mb-4 px-4">
          {services.map((service) => (
            <motion.div
              key={service.id}
              className={cn(
                'rounded-lg border-2 border-white/20',
                'cursor-pointer transition-all duration-300 ease-in-out',
                'hover:bg-white/10',
              )}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              onClick={() => handleCardClick(service.id)}>
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="mr-4 text-white/90">{service.icon}</div>
                  <h3 className="text-2xl font-bold">{service.title}</h3>
                </div>
                <svg
                  className="h-5 w-5 text-white/70"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile Fullscreen View */}
        <AnimatePresence>
          {activeCard && isMobile && (
            <motion.div
              initial={{
                opacity: 0,
                y: 100,
                position: 'fixed',
                inset: 0,
                zIndex: 50,
              }}
              animate={{
                opacity: 1,
                y: 0,
                position: 'fixed',
                inset: 0,
                zIndex: 50,
              }}
              exit={{
                opacity: 0,
                y: 100,
                position: 'fixed',
                inset: 0,
                zIndex: 50,
              }}
              transition={{ duration: 0.3 }}
              className="bg-primary overflow-auto flex flex-col">
              {/* Header with close button */}
              <div className="sticky top-0 bg-primary p-4 border-b border-white/20 flex justify-between items-center z-10">
                <div className="flex items-center">
                  <div className="mr-3">
                    {services.find((s) => s.id === activeCard)?.icon}
                  </div>
                  <h3 className="text-2xl font-bold">
                    {services.find((s) => s.id === activeCard)?.title}
                  </h3>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveCard(null);
                  }}
                  className="p-2 rounded-full hover:bg-white/10">
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-6">
                <p className="text-xl mb-8 leading-relaxed">
                  {services.find((s) => s.id === activeCard)?.description}
                </p>

                <h4 className="text-2xl font-bold mb-6">Key Services:</h4>
                <ul className="space-y-4">
                  {services
                    .find((s) => s.id === activeCard)
                    ?.items.map((item, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * idx }}
                        className="flex items-start text-xl">
                        <div className="h-8 w-8 mr-4 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary">
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="mt-1">{item}</span>
                      </motion.li>
                    ))}
                </ul>

                <motion.div
                  className="mt-12 text-left"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}>
                  <Button
                    variant="outline"
                    className="border-white text-primary hover:bg-secondary hover:text-white font-semibold"
                    onClick={(e) => {
                      e.stopPropagation();
                      const currentServiceId = activeCard;
                      if (currentServiceId) {
                        navigateToServiceTab(currentServiceId);
                      }
                    }}>
                    Learn More
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop View (Horizontal Accordion) */}
        <motion.div
          className="hidden md:flex w-full flex-grow overflow-hidden"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}>
          {services.map((service) => (
            <motion.div
              key={service.id}
              className={cn(
                'relative h-full flex flex-col overflow-hidden',
                'border-l border-dotted border-white/20 first:border-l-0 cursor-pointer',
                'transition-all duration-500 ease-in-out',
                !isMobile && 'hover:bg-white/5 py-10',
              )}
              initial={{ opacity: 0 }}
              animate={{ width: getCardWidth(service.id), opacity: 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onMouseEnter={() => !isMobile && setActiveCard(service.id)}
              onClick={() => isMobile && handleCardClick(service.id)}>
              {/* Card Header - Always Visible */}
              <div
                className={cn(
                  'h-full flex flex-col justify-center items-center p-6',
                  'transition-opacity duration-300',
                  activeCard && activeCard !== service.id
                    ? 'opacity-40 hover:opacity-80'
                    : 'opacity-100',
                )}>
                <div
                  className={cn(
                    'flex flex-col items-center justify-center transition-all duration-500',
                    activeCard === service.id
                      ? 'absolute top-6 left-0 w-full'
                      : '',
                  )}>
                  <div className="mb-4">{service.icon}</div>
                  <h3
                    className={cn(
                      'font-bold transition-all duration-500 whitespace-nowrap text-secondary',
                      activeCard === service.id ? 'text-3xl' : 'text-3xl',
                    )}>
                    {service.title}
                  </h3>
                </div>

                {/* Expanded Content - Only visible when active */}
                {activeCard === service.id && (
                  <motion.div
                    className="mt-24 w-full"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}>
                    <div className="max-w-2xl mx-auto">
                      <p className="text-xl mb-8 leading-relaxed">
                        {service.description}
                      </p>
                      <h4 className="text-2xl font-bold mb-6">Key Services:</h4>
                      <ul className="space-y-4">
                        {service.items.map((item, idx) => (
                          <motion.li
                            key={idx}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + 0.1 * idx }}
                            className="flex items-start text-xl">
                            <div className="h-8 w-8 mr-4 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary">
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                            <span className="mt-1">{item}</span>
                          </motion.li>
                        ))}
                      </ul>

                      <motion.div
                        className="mt-12 text-left"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}>
                        <Button
                          variant="outline"
                          className="border-white text-primary hover:bg-secondary hover:text-white font-semibold"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToServiceTab(service.id);
                          }}>
                          Learn More
                        </Button>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Floating CTA Button */}
      <motion.div
        ref={ctaRef}
        className="absolute bottom-8 left-0 right-0 flex justify-center pointer-events-none z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={isCTAInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}>
        <motion.div
          className="flex flex-col items-center pointer-events-auto"
          initial="initial"
          animate={isCTAInView ? 'pulse' : 'initial'}
          variants={pulseVariants}>
          <motion.button
            onClick={scrollToContact}
            className="bg-secondary text-white hover:bg-white hover:text-secondary rounded-full flex items-center justify-center gap-2 px-6 py-3 transition-colors duration-300 shadow-lg"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}>
            <motion.div
              initial="initial"
              animate={isCTAInView ? 'bounce' : 'initial'}
              variants={iconVariants}>
              <MessageCircle className="h-5 w-5 md:h-6 md:w-6" />
            </motion.div>
            <span className="font-bold text-sm md:text-base">Let's Talk!</span>
          </motion.button>

          <motion.div
            className="mt-3 text-white"
            initial="initial"
            animate={isCTAInView ? 'animate' : 'initial'}
            variants={arrowVariants}>
            <ChevronDown className="h-6 w-6" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Services;
